/* Shared analytics components for Performance & People Analytics module. */
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

export const TONE = {
  emerald: { bar: "bg-emerald-500", iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  ochre:   { bar: "bg-[#C5A059]",   iconBg: "bg-[#FAF4E5]",  iconText: "text-[#8A6B2B]"   },
  rose:    { bar: "bg-rose-500",    iconBg: "bg-rose-50",    iconText: "text-rose-600"    },
  sky:     { bar: "bg-sky-500",     iconBg: "bg-sky-50",     iconText: "text-sky-600"     },
  slate:   { bar: "bg-slate-400",   iconBg: "bg-slate-50",   iconText: "text-slate-600"   },
};

export function PageHero({ kicker, title, description, actions }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-5 sm:p-6 text-white">
      <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
      <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium">{kicker}</div>
          <h1 className="mt-1 font-display text-[28px] leading-tight sm:text-[34px] font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-1.5 text-[12.5px] sm:text-[13px] text-white/70 max-w-[680px]">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, accent = "emerald", testId, tone }) {
  const a = TONE[accent] || TONE.emerald;
  const subCls = tone === "warn" ? "text-amber-700" : tone === "bad" ? "text-rose-700" : "text-muted-foreground";
  return (
    <div data-testid={testId}
         className="relative overflow-hidden rounded-xl border border-border bg-white p-3.5 sm:p-4 hover:shadow-md transition-shadow">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${a.bar}`} />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-medium truncate">{label}</div>
          <div className="font-display text-[22px] sm:text-[28px] font-semibold tabular-nums mt-1 text-slate-900 leading-tight">
            {value === null || value === undefined ? "—" : (typeof value === "number" ? value.toLocaleString("pt-BR") : value)}
          </div>
          {sub && <div className={`text-[10.5px] sm:text-[11px] mt-1 ${subCls} truncate`}>{sub}</div>}
        </div>
        {Icon && (
          <div className={`grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-lg shrink-0 ${a.iconBg}`}>
            <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${a.iconText}`} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Section({ kicker, title, description, children, testId, action }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          {kicker && <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">{kicker}</div>}
          <div className="font-display text-lg sm:text-xl font-semibold mt-0.5">{title}</div>
          {description && <div className="text-[11.5px] text-muted-foreground">{description}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function GridSkeleton({ rows = 4 }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={`gs-${i}`} className="h-[110px]" />)}
    </div>
  );
}

export function EmptyState({ message, schema }) {
  return (
    <div className="rounded-xl border border-dashed border-[#C5A059]/40 bg-gradient-to-br from-[#FAF4E5]/40 to-white p-10 text-center">
      <div className="font-display text-lg font-semibold text-slate-900">Sem dados suficientes</div>
      <p className="mt-2 text-[13px] text-muted-foreground max-w-[480px] mx-auto">{message}</p>
      {schema && (
        <details className="mt-3 text-[11px] text-slate-600 inline-block">
          <summary className="cursor-pointer text-[#8A6B2B] font-medium">Ver estrutura preparada</summary>
          <div className="mt-2 text-left rounded-md bg-slate-50 border border-border p-3 font-mono text-[10.5px]">
            <div className="font-semibold mb-1">{schema.collection}</div>
            <ul className="space-y-0.5 list-disc list-inside">
              {schema.fields.map((f) => <li key={f}>{f}</li>)}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}

export function StatusBadge({ status, hot, ok }) {
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : hot
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{status}</Badge>;
}
